import type { FC, ReactNode } from 'react'
import type { HistoryEvent } from '@/types'

import Link from 'next/link'
import { ArrowRight, BookOpen, MapPin } from 'lucide-react'
import { buttonClass } from '@/components/generals/button'
import { Reveal } from '@/components/generals/reveal'
import { SectionHeading } from '@/components/generals/section-heading'

interface HistoryPreviewSectionProps {
    events: Array<HistoryEvent>
}

const HistoryPreviewSection: FC<HistoryPreviewSectionProps> = ({ events }): ReactNode => {
    return (
        <section id='family-history' className='border-y border-line bg-linen/70 px-5 py-20 md:px-8 md:py-24'>
            <div className='mx-auto grid max-w-6xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20'>
                <div>
                    <SectionHeading
                        eyebrow="Our history"
                        title="The moments that shaped the Theron family"
                        text="Births and weddings, moves to new towns, the homes we built and the days we will never forget, told in the order they happened."
                    />

                    <Link href='/history' className={buttonClass({ variant: 'secondary', className: 'group mt-8' })}>
                        Read the family history
                        <ArrowRight className='size-4 transition-transform duration-200 group-hover:translate-x-1' aria-hidden='true' />
                    </Link>
                </div>

                {events.length > 0 ? (
                    <ol className='space-y-4'>
                        {events.map((event, index) => (
                            <Reveal as='li' key={event.id} delay={index * 90} className='flex gap-5 rounded-2xl border border-line bg-parchment/60 p-5 md:p-6'>
                                <span className='w-20 shrink-0 font-heading text-2xl font-semibold text-clay'>
                                    <span data-mdlfy-cms={`history_events:${event.id}:year`}>
                                        {event.year}
                                    </span>
                                </span>

                                <span className='min-w-0'>
                                    <span data-mdlfy-cms={`history_events:${event.id}:title`} className='block font-heading text-lg leading-snug font-semibold text-ink'>
                                        {event.title}
                                    </span>

                                    {event.place && (
                                        <span className='mt-1.5 flex items-center gap-1.5 text-sm text-umber'>
                                            <MapPin className='size-4 shrink-0 text-forest' aria-hidden='true' />
                                            <span data-mdlfy-cms={`history_events:${event.id}:place`}>
                                                {event.place}
                                            </span>
                                        </span>
                                    )}
                                </span>
                            </Reveal>
                        ))}
                    </ol>
                ) : (
                    <Reveal className='flex flex-col justify-center rounded-[2rem] border border-dashed border-gold/70 bg-parchment/60 p-8 md:p-10'>
                        <BookOpen className='size-8 text-gold' aria-hidden='true' />

                        <h3 className='mt-5 font-heading text-2xl font-semibold text-ink'>
                            The first chapters are waiting to be written
                        </h3>

                        <p className='mt-3 text-base leading-relaxed text-umber'>
                            Where did the family come from? When did they arrive, marry, move or celebrate? Share the dates and places you know, and they will become part of our timeline.
                        </p>

                        <Link href='/share' className={buttonClass({ variant: 'quiet', className: 'group mt-6 self-start text-base' })}>
                            Share a piece of our history
                            <ArrowRight className='size-4 transition-transform duration-200 group-hover:translate-x-1' aria-hidden='true' />
                        </Link>
                    </Reveal>
                )}
            </div>
        </section>
    )
}

export default HistoryPreviewSection
